"use client";
import { IResource } from "@gettactic/api";
import { HookFormImageUploaderInput } from "@gettactic/components/src/form/ImageUploaderInput/HookFormImageUploaderInput";
import { HookFormNumberInput } from "@gettactic/components/src/form/NumberInput/HookFormNumberInput";
import { HookFormTagsInput } from "@gettactic/components/src/form/TagsInput/HookFormTagsInput";
import { HookFormTextEditorInput } from "@gettactic/components/src/form/TextEditorInput/HookFormTextEditorInput";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput/HookFormTextInput";
import { HookFormToggleInput } from "@gettactic/components/src/form/ToggleInput/HookFormToggleInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput/HookFormUnifiedSelectInput";
import { capitalize } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import { isLabelFieldEnabled } from "@gettactic/helpers/src/featureFlags";
import { getResourceCoverUrl } from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { useAreas, useAuthenticated, useGetAmenities } from "@gettactic/hooks";
import { ImageIcon } from "lucide-react";
import React from "react";
import type { UseFormReturn } from "react-hook-form";
import { ResourcePlacementLine } from "@/features/map/components/builder-resource-editor/ResourcePlacementLine";
import { ResourceMakeUnavailableControl } from "@/features/reserve/components/controls/ResourceMakeUnavailableControl";
import type { EditResourceForm } from "@/features/reserve/components/ResourceSettingsPanel/ResourceSettingsPanel";
import { ResourceTicketTypesField } from "@/features/settings/components/ResourceTicketTypesField";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/lib/hooks/useCustomParkingVerbiage";
import { toEntity } from "@/lib/utils";

const SectionLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <p className="mb-2 font-semibold text-[11px] text-neutral-400 uppercase tracking-wide">
    {children}
  </p>
);

/**
 * The general-settings field layout for a single resource — shared by the
 * map builder's resources panel and the "Edit Workspace" drawer so both
 * render the same polished sectioned form. Purely presentational over the
 * host's react-hook-form instance; hosts own submission.
 */
export const BuilderResourceGeneralFields: React.FC<{
  form: UseFormReturn<EditResourceForm>;
  resource: IResource;
  /** Show cover-image upload + amenities (hosts that persist them). */
  showMedia?: boolean;
}> = ({ form, resource, showMedia = false }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { areas: areaList } = useAreas(resource.office_id ?? null);
  const customParkingVerbiage = useCustomParkingVerbiage();
  const amenitiesResponse = useGetAmenities();
  const amenities = amenitiesResponse?.data?.result?.tags || [];

  const entity = toEntity(resource.type) || "workspace";
  const isMeetingRoom = entity === "meeting_room";
  const isParking =
    resource.type === "parking_space" || resource.type === "parking_lot";
  const isArea = resource.type === "area";
  // Dedicated desks belong to one person: grouping and booking options
  // don't apply (is_open must stay false, single slot, no approval flow).
  const isDedicated = !!resource.is_dedicated;
  const isMultiple = form.watch("is_multiple");
  const isApprovable = form.watch("is_approvable");

  const parentIdItems = React.useMemo(
    () =>
      (areaList?.options || [])
        .filter((opt: { value: string }) => opt.value !== resource.id)
        .map((opt: { value: string; label: string }) => ({
          id: opt.value,
          name: opt.label
        })),
    [areaList?.options, resource.id]
  );

  return (
    <div className="flex flex-col gap-5">
      <div>
        <SectionLabel>Details</SectionLabel>
        <p className="-mt-1 mb-2 select-all font-mono text-[10px] text-neutral-400">
          ID: {resource.id}
        </p>
        {!isArea ? <ResourcePlacementLine resourceId={resource.id} /> : null}
        <div className="flex flex-col gap-3">
          {showMedia && !isArea && (
            <HookFormImageUploaderInput
              form={form}
              name="cover_image_file"
              label="Cover Image"
              previewUrl={getResourceCoverUrl(resource)}
              fallback={<ImageIcon className="h-8 w-8 text-white" />}
              recommendedSize="Recommended size: 812px by 480px"
            />
          )}
          <HookFormTextInput form={form} name="name" label="Name" isRequired />
          {!isArea && (
            <>
              <div
                className={cn(
                  !isLabelFieldEnabled(authenticatedUser) && "hidden"
                )}
              >
                <HookFormTextInput form={form} name="label" label="Label" />
              </div>
              {showMedia && (
                <HookFormTagsInput
                  form={form}
                  name="amenities"
                  label="Amenities"
                  tags={amenities.map((amenity) => ({
                    value: amenity.id,
                    label: amenity.name,
                    keywords: [amenity.name]
                  }))}
                  placeholder="Search for amenities"
                />
              )}
              <HookFormTextEditorInput
                form={form}
                name="description"
                label="Description"
              />
            </>
          )}
        </div>
      </div>

      {!isArea && !isDedicated && (
        <div>
          <SectionLabel>Capacity</SectionLabel>
          <div className="flex flex-col gap-3">
            {!isMeetingRoom && (
              <>
                <HookFormToggleInput
                  form={form}
                  name="is_multiple"
                  label={
                    isParking
                      ? `${capitalize(customParkingVerbiage ?? "Parking")} Group`
                      : "Group of Workspaces"
                  }
                  description={
                    isParking
                      ? `Two or more ${customParkingVerbiage ?? "parking"} spaces instead of a single one.`
                      : "A group of desks instead of a single workspace."
                  }
                  togglePosition="right"
                />
                {isMultiple && (
                  <HookFormNumberInput
                    form={form}
                    name="total_slots"
                    label="Available spaces in this group"
                    isRequired
                    min={1}
                  />
                )}
              </>
            )}
            {isMeetingRoom && (
              <HookFormNumberInput
                form={form}
                name="total_slots"
                label="Room occupancy limit"
                isRequired
                min={1}
              />
            )}
          </div>
        </div>
      )}

      {areaList && parentIdItems.length > 0 && (
        <div>
          <SectionLabel>Placement</SectionLabel>
          <HookFormUnifiedSelectInput
            form={form}
            name="parent_id"
            label="Parent Zone or Floor"
            items={parentIdItems}
            placeholder="Choose an office zone or floor..."
          />
        </div>
      )}

      {!isArea && (
        <div>
          <SectionLabel>Booking</SectionLabel>
          {isDedicated ? (
            <p className="rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-neutral-700 text-xs">
              This is a dedicated{" "}
              {resource.type === "parking_space" ? "space" : "desk"} — nobody
              can book it, so booking options don't apply. Manage its owner (or
              make it bookable again) in the Assignments tab.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <HookFormToggleInput
                form={form}
                name="is_approvable"
                label="Approval Required"
                togglePosition="right"
                description={`Reservations for ${resource.name || "this workspace"} will need to be approved.`}
              />
              <ResourceTicketTypesField
                form={form}
                name="ticket_type_ids"
                resourceId={resource.id}
                officeId={resource.office_id}
                isApprovable={isApprovable}
              />
              <HookFormToggleInput
                form={form}
                name="is_walkup"
                label="Walk-up"
                togglePosition="right"
                description={`Walk-up resources can't be reserved from the app. Occupancy for ${resource.name || "this resource"} is managed externally via the API.`}
              />
              <ResourceMakeUnavailableControl
                form={form}
                resource={resource}
                entity={entity}
                name="is_unavailable"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
